import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import FAIReportModalContent, { FAIImageSrc } from './FAIReportModalContent';
import useModalActions from '@hooks/common/useModalAction';
import { scheduleRender } from '@features/analysis/rendering/Panels/renderState';
import { VesselData } from '../AnalysisReportModal/AnalysisReportModalContent';
import BaseModalContent from '../BaseReportModalContent/BaseReportModalContetnt';
import { REPORT_MODAL_NAMES } from 'config/labels';
import { useReportModal } from '@hooks/useReportModal';
import {
  ContextKey,
  CPRContext,
  CrossSectionContext,
  useBufferPool,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { captureView, cprreportcapture, trimMarginsToContent } from 'utils/captureUtils';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import useAuth from '@hooks/Auth/useAuth';
import { getConfiguredCprCrossSectionImageMode } from '@features/analysis/rendering/Panels/cprImageModeConfig';
import { captureServerCprReportRange } from '../serverCprReportCapture';
import { captureServerCrossSectionCanvas } from '@features/analysis/rendering/Panels/Renderers/ServerCrossSectionCanvas';

export function convertToTableData(data: any, isLesion: boolean): VesselData {
  const safe = (v: number | undefined) =>
    typeof v === 'number' ? round(v) : null;
  return {
    'Length (mm)': safe(data?.lengthValue),
    'Lumen Volume (mm³)': safe(data?.lumenValue),
    'Plaque Volume (mm³)': safe(data?.plaqueValue),
    'Vessel Volume (mm³)': safe(data?.vesselValue),
    'Calcified Plaque Volume (mm³, %)':
      data?.calcifiedValue != null && data?.vesselValue
        ? `${round(data.calcifiedValue)} mm³ (${round((data.calcifiedValue / data.vesselValue) * 100)}%)`
        : '-',
    'Noncalcified Plaque Volume (mm³, %)':
      data?.noncalcifiedValue != null && data?.vesselValue
        ? `${round(data.noncalcifiedValue)} mm³ (${round((data.noncalcifiedValue / data.vesselValue) * 100)}%)`
        : '-',
    'Low Attenuation Plaque Volume (mm³, %)':
      data?.lowattenuationValue != null && data?.vesselValue
        ? `${round(data.lowattenuationValue)} mm³ (${round((data.lowattenuationValue / data.vesselValue) * 100)}%)`
        : '-',
    'Remodeling Index': isLesion ? safe(data?.remodelingIndex) : '-',
    'Stenosis Degree (%)': isLesion
      ? data?.stenosisDegree != null
        ? Math.round(data.stenosisDegree * 100)
        : null
      : '-',
    'Minimum Lumen Area (mm²)': isLesion ? safe(data?.minLumenArea) : '-',
    'Minimum Lumen Diameter (mm)': isLesion
      ? safe(data?.minLumenDiameter)
      : '-',
    'Plaque Burden': isLesion
      ? data?.plaqueburden != null
        ? round(data.plaqueburden * 100)
        : null
      : '-',
  };
}

function round(value: number | undefined, precision = 1) {
  if (value == null) return null;
  return Math.round(value * 10 ** precision) / 10 ** precision;
}

type FAISrc = {
  imageData?: FAIImageSrc;
  tableData?: VesselData;
};
const EMPTY_REPORT_IMAGE =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADElEQVR42mNgYPgPAAEDAQC7sZ5aAAAAAElFTkSuQmCC';

async function captureCrossSectionReportImage(
  context: MutableRefObject<CrossSectionContext | null>,
  rendererId: string,
) {
  if (getConfiguredCprCrossSectionImageMode() === 'server') {
    const serverCanvasCapture = await captureServerCrossSectionCanvas(rendererId).catch(
      (err) => {
        console.warn('[report][front_data] FAI CrossSection capture failed', err);
        return undefined;
      },
    );
    if (serverCanvasCapture) {
      const [url] = await trimMarginsToContent(serverCanvasCapture);
      return url;
    }
  }

  return context.current
    ? await captureView(
        {
          type: 'vtk',
          renderWindow: context.current.renderWindow,
        },
        { scale: 1 },
      )
    : EMPTY_REPORT_IMAGE;
}

const FAIReportModal = ({ onClose }: { onClose: () => void }) => {
  const { contextPool, bufferPool } = useBufferPool();
  const { panelStates, analysisType } = usePanelContainer();
  const panelState = panelStates['CPR'].coronary!;
  const { selectedSeriesId } = useBrowserPage();
  const { currentVOI } = useAnalysisPage();
  const { accessToken } = useAuth();
  const poolRef = useRef(contextPool);
  useEffect(() => {
    poolRef.current = contextPool;
  }, [contextPool]);

  const names = REPORT_MODAL_NAMES.fai;
  const [src, setSrc] = useState<{
    lad?: FAISrc;
    lcx?: FAISrc;
    rca?: FAISrc;
  }>({});

  const { waitingAction, closeLoadingModal } = useModalActions();

  const isDone =
    !!src.lad &&
    !!src.lcx &&
    !!src.rca &&
    !!src.lad?.imageData?.lesion?.lesion &&
    !!src.lcx?.imageData?.lesion?.lesion &&
    !!src.rca?.imageData?.lesion?.lesion &&
    !!src.lad?.imageData?.lesion?.distal &&
    !!src.lcx?.imageData?.lesion?.distal &&
    !!src.rca?.imageData?.lesion?.distal &&
    !!src.lad?.imageData?.lesion?.maximal &&
    !!src.lcx?.imageData?.lesion?.maximal &&
    !!src.rca?.imageData?.lesion?.maximal &&
    !!src.lad?.imageData?.lesion?.proximal &&
    !!src.lcx?.imageData?.lesion?.proximal &&
    !!src.rca?.imageData?.lesion?.proximal &&
    !!src.lad?.imageData?.histogram &&
    !!src.lcx?.imageData?.histogram &&
    !!src.rca?.imageData?.histogram;
  // !!src.lad?.tableData &&
  // !!src.lcx?.tableData &&
  // !!src.rca?.tableData;

  const setter = (key: number, params: any) => {
    const type = key === 0 ? 'rca' : key === 1 ? 'lad' : 'lcx';
    setSrc((prev) => {
      return {
        ...prev,
        [type]: {
          ...prev[type],
          imageData: {
            ...prev[type]?.imageData,
            histogram: params.histogram ?? prev[type]?.imageData?.histogram,
          },
        },
      };
    });
  };

  async function test() {
    for (let key = 0; key < 3; key++) {
      const con = {
        current: poolRef.current[
          `CPR.${key}.straightened` as ContextKey
        ] as CPRContext,
      } as React.MutableRefObject<CPRContext | null>;
      const branchId = panelState.current[key];
      let info = panelState.state[branchId];

      const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;
      const serverResult =
        getConfiguredCprCrossSectionImageMode() === 'server' && pool
          ? await captureServerCprReportRange({
              accessToken,
              seriesId: selectedSeriesId,
              pool,
              analysisType,
              currentVOI,
              branchId,
              indexRange: [info[0].index, info[2].index],
            }).catch((err) => {
              console.warn('[report][front_data] FAI CPR capture failed', err);
              return null;
            })
          : null;
      const result = serverResult ?? await cprreportcapture(con, [
        info[0].index,
        info[2].index,
      ]);

      const cs0 = {
        current: poolRef.current[
          `CrossSection.${key}.0` as ContextKey
        ] as CrossSectionContext,
      } as React.MutableRefObject<CrossSectionContext | null>;

      const cs1 = {
        current: poolRef.current[
          `CrossSection.${key}.1` as ContextKey
        ] as CrossSectionContext,
      } as React.MutableRefObject<CrossSectionContext | null>;

      const cs2 = {
        current: poolRef.current[
          `CrossSection.${key}.2` as ContextKey
        ] as CrossSectionContext,
      } as React.MutableRefObject<CrossSectionContext | null>;

      let proximal = await captureCrossSectionReportImage(
        cs0,
        `CrossSection.${key}.0`,
      );

      let mid = await captureCrossSectionReportImage(
        cs1,
        `CrossSection.${key}.1`,
      );

      let distal = await captureCrossSectionReportImage(
        cs2,
        `CrossSection.${key}.2`,
      );

      scheduleRender(`Histogram.${key}`, {
        type: 'captureRequest',
        key,
        setter,
      });

      const type = key === 0 ? 'rca' : key === 1 ? 'lad' : 'lcx';
      setSrc((prev) => {
        return {
          ...prev,
          [type]: {
            ...prev[type],
            imageData: {
              ...prev[type]?.imageData,
              histogram: '',
              lesion: {
                lesion: result[1],
                proximal: proximal,
                maximal: mid,
                distal: distal,
              },
            },
          },
        };
      });
    }
  }

  useEffect(() => {
    waitingAction({ message: 'Waiting' });
    (async () => {
      await test();
    })();
  }, []);

  useEffect(() => {
    if (isDone) closeLoadingModal();
  }, [isDone]);

  if (!isDone) {
    return null;
  }

  return (
    <BaseModalContent
    className='fai'
     onClose={onClose} title={names.title}
    //  vw={60} vh={70}
     >
      <FAIReportModalContent
        imageSrc={{
          lad: src.lad!.imageData!,
          lcx: src.lcx!.imageData!,
          rca: src.rca!.imageData!,
        }}

        // tableData={{
        //   lad: src.lad!.tableData!,
        //   lcx: src.lcx!.tableData!,
        //   rca: src.rca!.tableData!,
        // }}
      />
    </BaseModalContent>
  );
};


export function useFAIReportModal(res:{updateActivatedTool: (tool: any,check: boolean) => void}) {
  return useReportModal({Component:FAIReportModal,...res});
}
